"use client";

import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ChevronRight, Loader2 } from "lucide-react";

import Footer from "../../components/layout/footer";
import Header from "../../components/layout/header";
import Link from "next/link";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const COLORS = {
  primary: "#003B5C",
  teal: "#00778B",
  gray: "#C7C9C7",
  gold: "#E3A130",
};

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

// ==========================
// UTILS
// ==========================
const waLink = (raw) => {
  if (!raw) return null;
  let num = raw.replace(/[^0-9]/g, "");
  if (num.startsWith("0")) num = "62" + num.slice(1);
  return `https://wa.me/${num}`;
};

const getDrivePreviewUrl = (url) => {
  if (!url) return "";
  if (url.includes("/view")) return url.replace("/view", "/preview");
  return url;
};



const formatPriceDot = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return "0.00";
  const sign = num < 0 ? "-" : "";
  const n = Math.abs(num);
  const [int, dec] = n.toFixed(2).split(".");
  const intWithDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${intWithDots}.${dec}`;
};

const StarRating = ({ value = 0 }) => {
  const full = Math.floor(value);
  const half = value % 1 >= 0.5;
  const stars = [];
  for (let i = 0; i < full; i++)
    stars.push(
      <span key={`f-${i}`} className="w-4 h-4 text-yellow-500">
        ★
      </span>
    );
  if (half)
    stars.push(
      <span key="half" className="w-4 h-4 text-yellow-500 opacity-70">
        ★
      </span>
    );
  while (stars.length < 5)
    stars.push(
      <span key={`e-${stars.length}`} className="w-4 h-4 text-gray-300">
        ★
      </span>
    );
  return <div className="flex">{stars}</div>;
};

/**
 * ProductCard
 */
function ProductCard({ product, href, onAddToCart }) {
  const getFrontImage = () => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      const front = product.images.find((img) => img.type === "front");
      return front?.url || product.images[0].url;
    }
    return "/product-placeholder.jpg";
  };

  const handleAddToCart = () => {
    try {
      const stock = Number(product.stock) || 0;

      if (stock <= 0) {
        alert("Maaf, stok habis.");
        return;
      }

      const raw = localStorage.getItem("cart") || "[]";
      const cart = JSON.parse(raw);
      if (!Array.isArray(cart)) throw new Error("Cart is not array");

      const existing = cart.find((item) => item.id === product.id);

      if (existing) {
        const currentQty = Number(existing.quantity || 0);

        if (currentQty >= stock) {
          alert(`Maksimal pembelian untuk produk ini adalah ${stock} pcs.`);
          return;
        }

        existing.quantity = currentQty + 1;
      } else {
        cart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          image: getFrontImage(),
          quantity: 1,
          store: {
            id: product.vendor?.id ?? "unknown",
            name: product.vendor?.storeName ?? "Unknown Store",
            phone: product.vendor?.phone ?? "",
          },
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cartUpdated"));
      if (onAddToCart) onAddToCart(product);
    } catch (e) {
      console.error("Cart error:", e);
    }
  };

  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? {
        href,
        className: "relative cursor-pointer block",
      }
    : {
        className: "relative cursor-pointer",
      };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-transform transition-shadow border border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900"
      style={{ borderTop: `4px solid ${COLORS.primary}` }}
    >
      <Wrapper {...wrapperProps}>
        <img
          src={getFrontImage()}
          alt={product.name}
          className="w-full h-64 object-cover"
        />
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold bg-red-500 px-2 py-1 rounded">
              Out of Stock
            </span>
          </div>
        )}
      </Wrapper>
      <div className="p-4 space-y-2 flex flex-col h-full">
        <h3 className="text-sm sm:text-base font-medium line-clamp-1 hover:underline">
          {href ? <a href={href}>{product.name}</a> : <span>{product.name}</span>}
        </h3>
        <StarRating value={product.rating || 4.5} />
        <div
          className="text-xs text-zinc-500 dark:text-zinc-400 prose max-h-[3rem] overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: product.description || "<p>No description available</p>",
          }}
        />
        <div className="flex items-center justify-between pt-2 gap-2 mt-auto">
          <div>
            <span className={`text-lg font-semibold ${tw.tealText}`}>
              Rp{formatPriceDot(product.price)}
            </span>
            {product.stock > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                {product.stock} in stock
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
            className="px-3 py-1.5 text-xs rounded-full border border-[#00778B] text-[#00778B] hover:bg-[#00778B] hover:text-white transition"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ==========================
// MAIN PAGE
// ==========================
export default function HomePage() {
  const [mounted, setMounted] = useState(false);

  // ---------- Data ----------
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [meta, setMeta] = useState({
    limit: 12,
    total: 0,
    cursor: null,
    next_cursor: null,
    has_more: false,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ---------- Filters ----------
  const [selectedStore, setSelectedStore] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // ---------- Store slider ----------
  const [storeSearch, setStoreSearch] = useState("");
  const [storePage, setStorePage] = useState(1);
  const STORES_PER_PAGE = 6;

  // ---------- Store products section ----------
  const [activeStore, setActiveStore] = useState(null);
  const [storeProducts, setStoreProducts] = useState([]);
  const [storeMeta, setStoreMeta] = useState({
    limit: 6,
    total: 0,
    cursor: null,
    next_cursor: null,
    has_more: false,
  });
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeErr, setStoreErr] = useState("");
  const [storeProductSearch, setStoreProductSearch] = useState("");
  const [loadingStores, setLoadingStores] = useState(false);

  // ---------- Product slider ----------
  const PRODUCT_PER_SLIDE = 6;
  const [prodPage, setProdPage] = useState(1);

  // ---------- CART ----------
  const [cart, setCart] = useState([]);

  // ---------- LINK MODAL ----------
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [modalLink, setModalLink] = useState("");
  const [modalStoreName, setModalStoreName] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    setMounted(true);
    try {
      const raw = localStorage.getItem("cart") || "[]";
      const initialCart = JSON.parse(raw);
      if (Array.isArray(initialCart)) setCart(initialCart);
    } catch {
      setCart([]);
    }

    const handleCartUpdated = () => {
      try {
        const raw = localStorage.getItem("cart") || "[]";
        const newCart = JSON.parse(raw);
        if (Array.isArray(newCart)) setCart(newCart);
      } catch {
        setCart([]);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdated);
    return () => window.removeEventListener("cartUpdated", handleCartUpdated);
  }, []);

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart]
  );

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum + (item.quantity || 0) * (Number(item.price) || 0),
        0
      ),
    [cart]
  );

  // ---------- HERO ----------
  const [heroIndex, setHeroIndex] = useState(0);

  const currentHero =
    stores.length > 0 ? stores[heroIndex % stores.length] : null;

  const heroStoreName = currentHero?.storeName || currentHero?.name || "";

  const heroProducts = useMemo(() => {
    if (!heroStoreName) return [];
    return allProducts
      .filter((p) => p.vendor?.storeName === heroStoreName)
      .slice(0, 5);
  }, [heroStoreName, allProducts]);

  useEffect(() => {
    if (!stores.length) return;
    const id = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % stores.length);
    }, 5000);
    return () => clearInterval(id);
  }, [stores.length]);

  const handleHeroNavClick = (i) => {
    setHeroIndex(i);
  };

  // ========== FILTERING PRODUCTS ==========
  useEffect(() => {
    if (selectedCategory) {
      const filtered = allProducts.filter(
        (p) => p.category?.name === selectedCategory
      );
      setFilteredProducts(filtered);
      setMeta((m) => ({ ...m, total: filtered.length }));
    } else {
      setFilteredProducts(allProducts);
      setMeta((m) => ({ ...m, total: allProducts.length }));
    }
    setProdPage(1);
  }, [selectedCategory, allProducts]);

  useEffect(() => {
    if (selectedStore) {
      const filtered = allProducts.filter(
        (p) => p.vendor?.storeName === selectedStore
      );
      setFilteredProducts(filtered);
      setMeta((m) => ({ ...m, total: filtered.length }));
    } else {
      setFilteredProducts(allProducts);
      setMeta((m) => ({ ...m, total: allProducts.length }));
    }
    setProdPage(1);
  }, [selectedStore, allProducts]);

  // ========== FETCHERS ==========

  const fetchStores = async () => {
    try {
      setLoadingStores(true);
      const res = await fetch(`${API_BASE}/stores?is_fnb=false`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Failed to load stores");

      const list = Array.isArray(json) ? json : [];

      const activeStores = list.filter(
        (s) => s.is_active === true || s.is_active === 1 || s.is_active === "1"
      );

      setStores(activeStores);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStores(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setErr("");

      const collected = [];
      let cursor = null;
      const limit = 100;
      let guard = 0;

      do {
        const params = new URLSearchParams({ limit: String(limit) });
        if (cursor !== null) params.append("cursor", String(cursor));
        if (selectedStore) params.append("store", selectedStore);
        if (selectedCategory) params.append("category", selectedCategory);

        const res = await fetch(`${API_BASE}/products?${params.toString()}`);
        const json = await res.json();
        if (!res.ok)
          throw new Error(json.detail || "Failed to load products");

        collected.push(...(json.data || []));
        cursor = json.meta?.next_cursor ?? null;
        guard += 1;
      } while (cursor !== null && guard < 50);

      setAllProducts(collected);
      setFilteredProducts(collected);
      setMeta((m) => ({
        ...m,
        limit,
        total: collected.length,
        cursor: null,
        next_cursor: null,
        has_more: false,
      }));
      setProdPage(1);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/categories?include_count=true`);
      const json = await res.json();
      if (!res.ok)
        throw new Error(json.detail || "Failed to load categories");
      const cats = Array.isArray(json) ? json : json.data || [];
      setCategories(cats);
    } catch (e) {
      console.error(e);
    }
  };

  const loadStoreProducts = async (
    storeName,
    cursor = null,
    search = "",
    append = false
  ) => {
    try {
      setStoreLoading(true);
      setStoreErr("");

      const params = new URLSearchParams({
        limit: String(storeMeta.limit),
      });
      if (cursor !== null) params.append("cursor", String(cursor));
      if (search) params.append("q", search);

      const res = await fetch(
        `${API_BASE}/stores/${encodeURIComponent(
          storeName
        )}/products?${params.toString()}`
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Failed to load products");

      if (append) {
        setStoreProducts((prev) => [...prev, ...(json.data || [])]);
      } else {
        setStoreProducts(json.data || []);
      }

      const m = json.meta || {};
      setStoreMeta((prev) => ({
        ...prev,
        limit: m.limit ?? prev.limit,
        total: m.total ?? prev.total,
        cursor: m.cursor ?? null,
        next_cursor: m.next_cursor ?? null,
        has_more: !!m.has_more,
      }));
    } catch (e) {
      setStoreErr(e.message);
    } finally {
      setStoreLoading(false);
    }
  };

  // ---------- Handlers ----------
  const scrollToId = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const openStoreSection = async (store) => {
    const sName = store.storeName || store.name;
    setActiveStore({ ...store, name: sName });

    setStoreMeta({
      limit: 6,
      total: 0,
      cursor: null,
      next_cursor: null,
      has_more: false,
    });
    setStoreProducts([]);
    setStoreProductSearch("");

    await loadStoreProducts(sName, null, "", false);
    scrollToId("store-products-section");
  };

  const closeStoreSection = () => {
    setActiveStore(null);
    setStoreProducts([]);
    setStoreProductSearch("");
    setStoreMeta({
      limit: 6,
      total: 0,
      cursor: null,
      next_cursor: null,
      has_more: false,
    });
    scrollToId("stores");
  };

  // modal link handlers
  const getStoreUrl = (store) =>
    store?.link || store?.url || store?.website || "";

  const openLinkModal = (store, url) => {
    if (!url) return;
    setModalStoreName(store?.storeName || store?.name || "Store link");
    setModalLink(url);
    setCopyStatus("");
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setModalLink("");
    setModalStoreName("");
    setCopyStatus("");
  };

  const handleCopyLink = async () => {
    if (!modalLink) return;
    try {
      await navigator.clipboard.writeText(modalLink);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus(""), 1500);
    } catch {
      setCopyStatus("Failed to copy");
      setTimeout(() => setCopyStatus(""), 1500);
    }
  };

  const handleOpenStoreLink = () => {
    if (!modalLink) return;
    const previewUrl = getDrivePreviewUrl(modalLink);
    if (!previewUrl) return;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  // initial load
  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch when filters change
  useEffect(() => {
    fetchProducts();
    if (selectedCategory) scrollToId("products");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore, selectedCategory]);

  // ---------- Derived data ----------
  const filteredStores = stores.filter((s) => {
    const name = s.storeName || s.name || "";
    return name.toLowerCase().includes(storeSearch.toLowerCase());
  });

  const storePages =
    Math.ceil(filteredStores.length / STORES_PER_PAGE) || 1;

  const pagedStores = filteredStores.slice(
    (storePage - 1) * STORES_PER_PAGE,
    storePage * STORES_PER_PAGE
  );

  if (!mounted) return null;

  const handleStorePageChange = (newPage) => {
    if (newPage < 1 || newPage > storePages) return;
    setLoadingStores(true);
    setTimeout(() => {
      setStorePage(newPage);
      setLoadingStores(false);
    }, 500);
  };

  const heroBgImage =
    currentHero?.photo ||
    "https://via.placeholder.com/1600x900/003B5C/FFFFFF?text=UIII+Cafetaria";

  return (
    <div>
      <Header cartCount={cartCount} />

      {/* STORES SLIDER */}
      <section
        id="stores"
        className="py-16 sm:py-24 bg-white dark:bg-zinc-900/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* HEADER + SEARCH (search di bawah text) */}
          <div className="mb-10">
            <div>
              <h2
                className={`text-2xl sm:text-3xl font-semibold ${tw.primaryText}`}
              >
                Others 
              </h2>
              <p className="mt-2 text-sm sm:text-base text-zinc-600 dark:text-zinc-300 max-w-10xl">
                This category features a variety of non–food and beverage outlets that support daily campus needs and activities. From retail and services to lifestyle and convenience shops, these tenants complement the campus ecosystem by providing practical solutions, essentials, and added value for students, staff, and visitors.
              </p>
            </div>

            {/* Search di bawah text, kiri di mobile, kanan di desktop */}
            <div className="mt-4 w-full flex justify-start md:justify-end">
              <div className="w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search stores..."
                  className="w-full p-2 border rounded text-sm dark:bg-zinc-900 dark:border-zinc-700 focus:ring-2 focus:ring-[#00778B] focus:outline-none"
                  value={storeSearch}
                  onChange={(e) => {
                    setStoreSearch(e.target.value);
                    setStorePage(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* GRID STORES */}
          <div className="relative">
            <div className="overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8">
                {pagedStores.map((store, idx) => {
                  const storeUrl = getStoreUrl(store);
                  return (
                    <div
                      key={idx}
                      className="col-span-1 h-full"
                      onClick={() => openStoreSection(store)}
                    >
                      <div className="h-full flex flex-col bg-white dark:bg-zinc-800 rounded-2xl shadow-md shadow-zinc-200/70 dark:shadow-black/40 border border-zinc-100 dark:border-zinc-700 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer p-4 text-center">
                        <div className="w-full h-32 sm:h-36 bg-zinc-100 dark:bg-zinc-700 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                          <img
                            src={store.photo || "/store-placeholder.jpg"}
                            alt={store.storeName}
                            className="h-full object-contain"
                          />
                        </div>

                        <div className="flex-1 flex flex-col">
                          <h3 className="font-semibold text-base sm:text-lg line-clamp-1">
                            {store.storeName}
                          </h3>
                          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2">
                            {store.storeDescription
                              ? store.storeDescription
                              : "No description"}
                          </p>

                          {/* PHONE & LINK (ringkas di card) */}
                          {(store.phone || storeUrl) && (
                            <div className="mt-3 space-y-1 text-xs text-zinc-500">
                              {store.phone && (
                                <div>
                                  Tel:{" "}
                                  <a
                                    href={`tel:${store.phone}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-medium text-[#00778B]"
                                  >
                                    {store.phone}
                                  </a>
                                </div>
                              )}
                              {storeUrl && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLinkModal(store, storeUrl);
                                  }}
                                  className="inline-flex items-center gap-1 text-[#00778B] hover:underline"
                                >
                                  <span>Show Catalog</span>
                                  <ChevronRight className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {pagedStores.length === 0 && (
                  <p className="text-sm text-zinc-500 col-span-full">
                    No stores found.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-center items-center gap-4 mt-6 text-sm">
              <button
                disabled={storePage <= 1 || loadingStores}
                onClick={() => handleStorePageChange(storePage - 1)}
                className="px-3 py-1 bg-white dark:bg-zinc-900 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span>
                {storePage} / {storePages}
              </span>

              <button
                disabled={storePage >= storePages || loadingStores}
                onClick={() => handleStorePageChange(storePage + 1)}
                className="px-3 py-1 bg-white dark:bg-zinc-900 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVE STORE PRODUCTS */}
      {activeStore && (
        <section id="store-products-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* HEADER STORE + PHONE + LINK + SEARCH PRODUCT */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <button
                    type="button"
                    onClick={closeStoreSection}
                    className="text-xs mb-2 text-zinc-500 hover:text-zinc-700"
                  >
                    ← Back to all stores
                  </button>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-zinc-900 dark:text-white">
                    {activeStore.name}
                  </h2>
                  {activeStore.storeDescription && (
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-2xl">
                      {activeStore.storeDescription}
                    </p>
                  )}

                  {/* PHONE & LINK DETAIL */}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-zinc-600 dark:text-zinc-300">
                    {activeStore.phone && (
                      <>
                        <span>
                          Tel:{" "}
                          <a
                            href={`tel:${activeStore.phone}`}
                            className="font-medium text-[#00778B]"
                          >
                            {activeStore.phone}
                          </a>
                        </span>
                        <span className="hidden sm:inline text-zinc-400">
                          •
                        </span>
                      </>
                    )}

                    {activeStore.phone && (
                      <a
                        href={waLink(activeStore.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-green-600 hover:text-green-700"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Chat via WhatsApp</span>
                      </a>
                    )}

                    {getStoreUrl(activeStore) && (
                      <>
                        <span className="hidden sm:inline text-zinc-400">
                          •
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            openLinkModal(
                              activeStore,
                              getStoreUrl(activeStore)
                            )
                          }
                          className="px-3 py-1.5 text-xs rounded-full border border-[#00778B] text-[#00778B] hover:bg-[#00778B] hover:text-white transition"
                        >
                          <span>Show Catalog</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* SEARCH PRODUCT PER STORE */}
                <div className="w-full md:w-72">
                  <label className="block text-xs font-medium text-zinc-600 mb-1">
                    Search products in this store
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search products..."
                      className="flex-1 p-2 border rounded text-sm dark:bg-zinc-900 dark:border-zinc-700 focus:ring-2 focus:ring-[#00778B] focus:outline-none"
                      value={storeProductSearch}
                      onChange={async (e) => {
                        const val = e.target.value;
                        setStoreProductSearch(val);
                        await loadStoreProducts(activeStore.name, null, val, false);
                      }}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    Ketik nama menu / produk untuk filter daftar di bawah.
                  </p>
                </div>
              </div>
            </div>

            {/* BODY PRODUCTS */}
            {storeErr && (
              <p className="text-red-500 text-sm mb-4">{storeErr}</p>
            )}

            {storeLoading && storeProducts.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : (
              <>
                {storeProducts.length === 0 && (
                  <p className="text-sm text-zinc-500">No products.</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {storeProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      href={`/product/${product.id}`}
                    />
                  ))}
                </div>

                <div className="mt-10 flex justify-center items-center gap-4 text-sm">
                  {storeMeta.has_more ? (
                    <button
                      disabled={storeLoading}
                      onClick={() =>
                        loadStoreProducts(
                          activeStore.name,
                          storeMeta.next_cursor,
                          storeProductSearch,
                          true
                        )
                      }
                      className="px-4 py-2 bg-white dark:bg-zinc-900 border rounded disabled:opacity-50 flex items-center gap-2"
                    >
                      {storeLoading && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      Load more
                    </button>
                  ) : (
                    storeProducts.length > 0 && (
                      <span className="text-zinc-500 text-xs">
                        No more products.
                      </span>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* LINK MODAL */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-5">
            <div className="flex justify-between items-start gap-2 mb-3">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-zinc-900 dark:text-white">
                  Store Catalog
                </h3>
                {modalStoreName && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {modalStoreName}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeLinkModal}
                className="text-zinc-400 hover:text-zinc-600"
              >
                ✕
              </button>
            </div>

            {/* EMBED DRIVE PREVIEW */}
            {modalLink && (
              <div className="mb-4 h-64 sm:h-80">
                <iframe
                  src={getDrivePreviewUrl(modalLink)}
                  className="w-full h-full rounded border border-zinc-200 dark:border-zinc-700"
                  allow="autoplay"
                  loading="lazy"
                />
              </div>
            )}

            <div className="flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={handleCopyLink}
                className="px-3 py-1.5 text-xs rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                {copyStatus || "Copy URL"}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeLinkModal}
                  className="px-3 py-1.5 text-xs rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleOpenStoreLink}
                  className="px-3 py-1.5 text-xs rounded-md bg-teal-700 text-white hover:bg-teal-800"
                >
                  Open link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* CART WIDGET */}
      {cart.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 bg-white dark:bg-zinc-900 shadow-lg border border-zinc-200 dark:border-zinc-700 rounded-xl p-3 w-72 max-h-80 flex flex-col">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold">
              Cart ({cartCount} item{cartCount > 1 ? "s" : ""})
            </span>
            <span className="text-xs text-zinc-500">
              Rp{formatPriceDot(cartTotal)}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 text-xs">
            {cart.map((item) => (
              <div
                key={item.id}
                className="flex justify-between gap-2 border-b border-dashed border-zinc-200 dark:border-zinc-700 pb-1"
              >
                <div className="flex-1">
                  <div className="font-medium line-clamp-1">
                    {item.name}
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {item.quantity} × Rp{formatPriceDot(item.price)}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600">
                  Rp{formatPriceDot(item.quantity * item.price)}
                </div>
              </div>
            ))}
          </div>
          <Link href="/cart">
            <button
              className={`mt-2 w-full text-xs font-semibold py-1.5 rounded-full ${tw.primaryBg} text-white`}
              type="button"
            >
              Review Order
            </button>
          </Link>
        </div>
      )}

      <Footer />
    </div>
  );
}
